import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { BehaviorSubject, Subject, of } from 'rxjs';
import { catchError, finalize, takeUntil } from 'rxjs/operators';
import { AnnexService } from 'src/app/data/annex.service';
import { Filter } from 'src/app/models/common/filter';
import { AnnexRegistryGridDto } from 'src/app/models/dtos/annexes/annex-registry-grid-dto';
import { IPagedResult } from 'src/app/shared/models/PagedResult';
import { GridTableDataSource } from 'src/app/virtual-scroll/data-source';

export class AnnexRegistryDataSource extends GridTableDataSource<AnnexRegistryGridDto> {
  isLoading$ = new BehaviorSubject<boolean>(false);
  private cancelationSubject = new Subject();
  private countSubject = new BehaviorSubject<number>(0);
  private dataLoadedSubject = new Subject<void>();
  private _absoluteDataLength: number;

  count$ = this.countSubject.asObservable();
  dataLoaded$ = this.dataLoadedSubject.asObservable();

  get isLoading() {
    return this.isLoading$.getValue();
  }

  get absoluteDataLength() {
    return this._absoluteDataLength;
  }

  constructor(
    private annexService: AnnexService,
    { viewport }: { viewport?: CdkVirtualScrollViewport } = {}
  ) {
    super([], { viewport });
  }

  disconnect() {
    this.cancelationSubject.next(undefined);
    this.isLoading$.next(false);
  }

  reset() {
    this.cancelationSubject.next(undefined);
    this.isLoading$.next(false);
    this.allData = [];
  }

  fetchAnnexRegistry(page: number, count: number, sortingField: string, sortingDirection: string, filters: Filter[]) {
    this.isLoading$.next(true);
    this.annexService
      .getPagedAnnexRegistry(page, count, sortingField, sortingDirection, filters)
      .pipe(
        takeUntil(this.cancelationSubject),
        catchError(() => of(null)),
        finalize(() => this.isLoading$.next(false)),
      )
      .subscribe((response: IPagedResult<AnnexRegistryGridDto>) => {
        if (!response) return;
        this._absoluteDataLength = response.Count;
        this.merge(response.Results, page, count);
        this.countSubject.next(response.Count);
        this.dataLoadedSubject.next();
      });
  }

  private merge(data: AnnexRegistryGridDto[], page: number, count: number) {
    const tmp = this.allData.filter((item, index) => {
      return (index < count * (page - 1) || index >= count * page) || data.some(incoming => incoming.AnnexId === item.AnnexId);
    });

    data?.map((item, itemIdx) => {
      const index = tmp.findIndex(td => td.AnnexId === item.AnnexId);
      if (index > -1) {
        tmp[index] = item;
      } else {
        const idx = count * (page - 1) + itemIdx;
        if (idx > -1) {
          tmp.splice(idx, 0, item);
        } else {
          tmp.push(item);
        }
      }
    });

    this.allData = tmp;
  }
}
